/**
 * Composant CRMFinancesTab - Onglet Finances du CRM
 * Affiche les statistiques CA, le suivi de trésorerie et les alertes
 */

import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { 
  TrendingUp, CreditCard, Clock, Receipt, Wallet, Plus, Edit2, Trash2,
  FileSpreadsheet, Bell, AlertTriangle, CalendarClock, Calendar
} from 'lucide-react';
import { TresorerieFiltres, GroupeEncaissement } from './finances/TresorerieParts';

// Carte statistique CA
const CAStatCard = ({ title, value, subtitle, icon: Icon, colorClass }) => (
  <Card className={`bg-gradient-to-br ${colorClass.bg}`}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <p className={`text-2xl font-bold ${colorClass.value}`}>
            {typeof value === 'number' ? value.toLocaleString('fr-FR') : value} €
          </p>
          {subtitle && <p className={`text-xs ${colorClass.subtitle}`}>{subtitle}</p>}
        </div>
        <Icon className={`h-8 w-8 ${colorClass.icon}`} />
      </div>
    </CardContent>
  </Card>
);

export const CRMFinancesTab = ({
  statsCA,
  bilanFinancier,
  alertes,
  onAddBilanLigne,
  onEditBilanLigne,
  onDeleteBilanLigne,
  onExportExcel,
  session = 'all',
  sessions = [],
  onSessionChange,
  periode = { debut: '', fin: '' },
  onPeriodeChange
}) => {
  return (
    <div data-testid="crm-finances-tab">
      {/* Stats CA */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <CAStatCard
          title="CA Prévisionnel"
          value={statsCA?.ca_total || 0}
          subtitle="Devis validés clients"
          icon={TrendingUp}
          colorClass={{ bg: 'from-blue-50 to-white border-blue-100', value: 'text-blue-700', subtitle: 'text-blue-500', icon: 'text-blue-500' }}
        />
        
        <CAStatCard
          title="Encaissées"
          value={statsCA?.ca_encaisse || 0}
          subtitle={`${statsCA?.taux_encaissement || 0}% du CA`}
          icon={CreditCard}
          colorClass={{ bg: 'from-emerald-50 to-white border-emerald-100', value: 'text-emerald-700', subtitle: 'text-emerald-500', icon: 'text-emerald-500' }}
        />
        
        <CAStatCard
          title="En attente"
          value={statsCA?.ca_en_attente || 0}
          subtitle="Non encore perçu"
          icon={Clock}
          colorClass={{ bg: 'from-orange-50 to-white border-orange-100', value: 'text-orange-700', subtitle: 'text-orange-500', icon: 'text-orange-500' }}
        />
        
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Factures</p>
                <p className="text-2xl font-bold text-purple-700">
                  {statsCA?.factures?.total || 0}
                </p>
                <p className="text-xs text-purple-500">{statsCA?.factures?.payees || 0} payées / {statsCA?.factures?.en_cours || 0} en cours</p>
              </div>
              <Receipt className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Bilan Financier */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-emerald-600" />
              Suivi de Trésorerie
            </CardTitle>
            <Button 
              onClick={onAddBilanLigne}
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="add-bilan-ligne-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              Ajouter une ligne
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <TresorerieFiltres session={session} sessions={sessions} onSessionChange={onSessionChange} periode={periode} onPeriodeChange={onPeriodeChange} />
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-3 text-sm font-medium text-slate-600">Date</th>
                  <th className="text-left p-3 text-sm font-medium text-slate-600">Libellé</th>
                  <th className="text-right p-3 text-sm font-medium text-slate-600">Encaissement</th>
                  <th className="text-right p-3 text-sm font-medium text-slate-600">Décaissement</th>
                  <th className="text-right p-3 text-sm font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Lignes automatiques : factures, frais de tests, frais de préparation (dépliables) */}
                {(bilanFinancier.groupes || []).map(g => <GroupeEncaissement key={g.cle} groupe={g} />)}
                
                {/* Lignes manuelles */}
                {bilanFinancier.lignes?.map((ligne) => (
                  <tr key={ligne.ligne_id} className="border-b hover:bg-slate-50">
                    <td className="p-3 text-sm text-slate-500">
                      {ligne.date ? new Date(ligne.date).toLocaleDateString('fr-FR') : '-'}
                    </td>
                    <td className="p-3">
                      <div>
                        <span className="font-medium text-slate-800">{ligne.libelle}</span>
                        {ligne.notes && <p className="text-xs text-slate-400">{ligne.notes}</p>}
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      {ligne.type_operation === 'encaissement' ? (
                        <span className="font-medium text-emerald-600">+{ligne.montant?.toLocaleString('fr-FR')} €</span>
                      ) : '-'}
                    </td>
                    <td className="p-3 text-right">
                      {ligne.type_operation === 'decaissement' ? (
                        <span className="font-medium text-red-600">-{ligne.montant?.toLocaleString('fr-FR')} €</span>
                      ) : '-'}
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600"
                          onClick={() => onEditBilanLigne(ligne)}
                        >
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
                          onClick={() => onDeleteBilanLigne(ligne.ligne_id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                
                {bilanFinancier.lignes?.length === 0 && (
                  <tr>
                    <td colSpan="5" className="p-4 text-center text-slate-400">
                      Aucune ligne ajoutée. Cliquez sur "Ajouter une ligne" pour commencer.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot className="bg-slate-800 text-white">
                <tr>
                  <td colSpan="2" className="p-3 font-bold">SOLDE</td>
                  <td colSpan="2" className="p-3 text-right">
                    <span className={`text-xl font-bold ${bilanFinancier.solde >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {bilanFinancier.solde >= 0 ? '+' : ''}{(bilanFinancier.solde || 0).toLocaleString('fr-FR')} €
                    </span>
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
      
      {/* Bouton Export */}
      <div className="flex justify-end mb-4">
        <Button onClick={onExportExcel} variant="outline" className="border-green-300 text-green-600" data-testid="export-excel-btn">
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Export Excel
        </Button>
      </div>
      
      {/* Alertes */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-orange-500" />
            Alertes ({alertes.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {alertes.length === 0 ? (
            <p className="text-slate-500 text-center py-4">Aucune alerte en cours</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {alertes.map((alerte, idx) => (
                <div 
                  key={idx} 
                  className={`p-3 rounded-lg border flex items-center justify-between ${
                    alerte.priorite === 'haute' ? 'bg-red-50 border-red-200' :
                    alerte.priorite === 'moyenne' ? 'bg-orange-50 border-orange-200' :
                    'bg-slate-50 border-slate-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {alerte.type === 'retard_paiement' && <AlertTriangle className="h-5 w-5 text-red-500" />}
                    {alerte.type === 'echeance_proche' && <CalendarClock className="h-5 w-5 text-orange-500" />}
                    {alerte.type === 'facture_impayee' && <Receipt className="h-5 w-5 text-slate-500" />}
                    {alerte.type === 'fin_formation' && <Calendar className="h-5 w-5 text-blue-500" />}
                    <div>
                      <p className="font-medium text-sm">{alerte.prospect_nom || alerte.stagiaire_nom}</p>
                      <p className="text-xs text-slate-600">{alerte.message}</p>
                      {alerte.facture_numero && (
                        <p className="text-xs text-slate-400">Facture: {alerte.facture_numero}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    {alerte.montant && (
                      <p className="font-bold text-sm">{alerte.montant.toLocaleString('fr-FR')} €</p>
                    )}
                    <Badge className={
                      alerte.priorite === 'haute' ? 'bg-red-500' :
                      alerte.priorite === 'moyenne' ? 'bg-orange-500' : 'bg-slate-500'
                    }>
                      {alerte.priorite}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Stats par centre */}
      {statsCA?.par_centre && Object.keys(statsCA.par_centre).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>CA par Centre</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(statsCA.par_centre).map(([centre, data]) => (
                <div key={centre} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="font-medium capitalize">{centre}</span>
                  <div className="text-right">
                    <p className="font-bold text-green-700">{data.total.toLocaleString('fr-FR')} €</p>
                    <p className="text-xs text-slate-500">Encaissé: {data.encaisse.toLocaleString('fr-FR')} €</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CRMFinancesTab;
